import { FormFieldConfig, FlexFormFirestoreSelectOptionSet } from "@captiva/shared-types";

export interface FilterOptionItem {
  value: string;
  label: string;
}

export interface SubmissionDataField {
  type?: string;
  value: any;
  label?: string;
  metadata?: any;
}

export interface SubmissionLike {
  id: string;
  documentNumber?: string;
  submitterName?: string;
  submitterEmail?: string;
  data?: Record<string, SubmissionDataField>;
}

/**
 * Extracts valid filter options for a given field configuration.
 * For select/multiSelect, it resolves options from:
 * 1. Template optionsSource (static options array or firestore option set)
 * 2. Distinct values actually present in the loaded submissions (fallback/augmentation)
 */
export function extractFilterOptionsForField(
  field: FormFieldConfig | undefined | null,
  selectOptions: FlexFormFirestoreSelectOptionSet[] = [],
  loadedSubmissions: SubmissionLike[] = []
): FilterOptionItem[] {
  if (!field) return [];

  const type = field.type?.toLowerCase();

  switch (type) {
    case "boolean":
      return [
        { value: "true", label: "True / Yes" },
        { value: "false", label: "False / No" },
      ];

    case "select":
    case "multiselect": {
      const optionsMap = new Map<string, string>(); // value -> label

      // 1. Check field.optionsSource for static options
      if (field.optionsSource && typeof field.optionsSource === "object") {
        const os = field.optionsSource as any;
        if (os.type === "static" && Array.isArray(os.options)) {
          for (const opt of os.options) {
            if (opt && opt.value !== undefined && opt.value !== null) {
              const val = String(opt.value);
              const lbl = opt.label || val;
              optionsMap.set(val, lbl);
            }
          }
        } else if (os.type === "firestore") {
          const targetId = os.id || os.optionsSetId;
          const matchingSet = selectOptions.find((set) => set.id === targetId);
          if (matchingSet && Array.isArray(matchingSet.options)) {
            for (const opt of matchingSet.options) {
              if (opt && opt.value !== undefined && opt.value !== null) {
                const val = String(opt.value);
                const lbl = opt.label || val;
                optionsMap.set(val, lbl);
              }
            }
          }
        }
      }

      // 2. Augment with any distinct values found in loaded submissions
      for (const sub of loadedSubmissions) {
        const fieldData = sub.data?.[field.key];
        if (!fieldData || fieldData.value === undefined || fieldData.value === null) continue;

        if (Array.isArray(fieldData.value)) {
          fieldData.value.forEach((itemVal: any, idx: number) => {
            if (itemVal !== undefined && itemVal !== null) {
              const val = String(itemVal);
              const metaLabel = Array.isArray(fieldData.metadata)
                ? fieldData.metadata[idx]?.label
                : undefined;
              if (!optionsMap.has(val)) {
                optionsMap.set(val, metaLabel || val);
              }
            }
          });
        } else {
          const val = String(fieldData.value);
          const metaLabel = fieldData.metadata?.label;
          if (!optionsMap.has(val)) {
            optionsMap.set(val, metaLabel || val);
          }
        }
      }

      const result: FilterOptionItem[] = [];
      optionsMap.forEach((label, value) => {
        result.push({ value, label });
      });

      result.sort((a, b) => a.label.localeCompare(b.label));
      return result;
    }

    case "checklist":
    case "checklistwithnotes": {
      const itemsMap = new Map<string, string>();

      // Scan loaded submissions for distinct checklist items
      for (const sub of loadedSubmissions) {
        const fieldData = sub.data?.[field.key];
        if (!fieldData || !Array.isArray(fieldData.value)) continue;

        for (const item of fieldData.value) {
          if (item && (item.label || item.id)) {
            const itemLabel = item.label || item.id;
            itemsMap.set(itemLabel, itemLabel);
          }
        }
      }

      const options: FilterOptionItem[] = [
        { value: "__HAS_FAILED__", label: "Any Failed / Unchecked" },
        { value: "__ALL_PASSED__", label: "All Items Passed" },
      ];

      itemsMap.forEach((label) => {
        options.push({
          value: `item_fail:${label}`,
          label: `Defect in: ${label}`,
        });
      });

      return options;
    }

    case "photo":
    case "multiphoto":
      return [
        { value: "present", label: "Has Photo(s)" },
        { value: "missing", label: "Missing Photo" },
      ];

    case "signature":
      return [
        { value: "present", label: "Signed" },
        { value: "missing", label: "Unsigned" },
      ];

    case "gps":
      return [
        { value: "present", label: "Has GPS Coordinates" },
        { value: "missing", label: "Missing GPS" },
      ];

    default:
      return [];
  }
}

/**
 * Predicate evaluating whether a submission's field value matches the active filter.
 */
export function matchesFilter(
  field: FormFieldConfig | undefined | null,
  fieldData: SubmissionDataField | undefined | null,
  filterValue: string | null | undefined
): boolean {
  if (filterValue === null || filterValue === undefined || filterValue.trim() === "") {
    return true; // No filter applied
  }

  const trimmedFilter = filterValue.trim();
  const lowerFilter = trimmedFilter.toLowerCase();
  const type = field?.type?.toLowerCase() || fieldData?.type?.toLowerCase() || "text";

  // Check existence filters first (handles undefined/empty fieldData)
  if (type === "photo" || type === "multiphoto" || type === "signature") {
    const rawVal = fieldData?.value;
    const hasPhotoOrSig = Array.isArray(rawVal)
      ? rawVal.length > 0 && rawVal.some((u) => Boolean(u && String(u).trim()))
      : Boolean(rawVal && String(rawVal).trim());

    if (lowerFilter === "present") return hasPhotoOrSig;
    if (lowerFilter === "missing") return !hasPhotoOrSig;
  }

  if (type === "gps") {
    const rawVal = fieldData?.value;
    const hasValidGps =
      Array.isArray(rawVal) &&
      rawVal.length === 2 &&
      !isNaN(Number(rawVal[0])) &&
      !isNaN(Number(rawVal[1])) &&
      (rawVal[0] !== 0 || rawVal[1] !== 0);

    if (lowerFilter === "present") return hasValidGps;
    if (lowerFilter === "missing") return !hasValidGps;
  }

  // If field data is missing, it cannot match any content-based filter
  if (!fieldData || fieldData.value === undefined || fieldData.value === null) {
    return false;
  }

  const rawVal = fieldData.value;
  const metaLabel = fieldData.metadata?.label;

  switch (type) {
    case "boolean": {
      const boolVal = typeof rawVal === "boolean" ? rawVal : String(rawVal).toLowerCase() === "true";
      return String(boolVal) === lowerFilter;
    }

    case "select": {
      const stringVal = String(rawVal).toLowerCase();
      // Match on value ID (e.g. "fail") or metadata label (e.g. "Fail (Safety Critical)")
      if (stringVal === lowerFilter) return true;
      if (metaLabel && String(metaLabel).toLowerCase() === lowerFilter) return true;
      // Also allow partial substring match if user typed in text search
      if (stringVal.includes(lowerFilter)) return true;
      if (metaLabel && String(metaLabel).toLowerCase().includes(lowerFilter)) return true;
      return false;
    }

    case "multiselect": {
      if (Array.isArray(rawVal)) {
        // Direct value matching
        if (rawVal.some((v) => String(v).toLowerCase() === lowerFilter || String(v).toLowerCase().includes(lowerFilter))) {
          return true;
        }
      }
      // Metadata label array matching
      if (Array.isArray(fieldData.metadata)) {
        if (fieldData.metadata.some((m) => m?.label && String(m.label).toLowerCase().includes(lowerFilter))) {
          return true;
        }
      }
      return false;
    }

    case "checklist":
    case "checklistwithnotes": {
      if (!Array.isArray(rawVal)) return false;

      if (trimmedFilter === "__HAS_FAILED__") {
        return rawVal.some((item) => item && item.checked === false);
      }

      if (trimmedFilter === "__ALL_PASSED__") {
        return rawVal.length > 0 && rawVal.every((item) => item && item.checked === true);
      }

      if (trimmedFilter.startsWith("item_fail:")) {
        const targetLabel = trimmedFilter.replace("item_fail:", "").toLowerCase();
        return rawVal.some(
          (item) =>
            item &&
            item.checked === false &&
            (String(item.label || "").toLowerCase() === targetLabel || String(item.id || "").toLowerCase() === targetLabel)
        );
      }

      // Freeform search: match item label or notes text
      return rawVal.some((item) => {
        if (!item) return false;
        const itemLabel = String(item.label || "").toLowerCase();
        const note = String(item.note || "").toLowerCase();
        return itemLabel.includes(lowerFilter) || note.includes(lowerFilter);
      });
    }

    case "number": {
      const numString = String(rawVal).toLowerCase();
      return numString.includes(lowerFilter);
    }

    case "text":
    case "time":
    case "date":
    case "datetime":
    case "barcode":
    case "nfc":
    default: {
      if (Array.isArray(rawVal)) {
        return rawVal.some((v) => String(v).toLowerCase().includes(lowerFilter));
      }
      return String(rawVal).toLowerCase().includes(lowerFilter);
    }
  }
}

/**
 * Filter an array of submissions by field and value.
 * Supports a special filterFieldKey:
 * - "__ALL__": searches across all fields and top-level submitter metadata.
 */
export function filterSubmissions<T extends SubmissionLike>(
  submissions: T[],
  filterFieldKey: string | null | undefined,
  filterValue: string | null | undefined,
  fields: FormFieldConfig[] = []
): T[] {
  if (!filterValue || !filterValue.trim()) {
    return submissions;
  }

  const trimmedFilter = filterValue.trim();
  const lowerFilter = trimmedFilter.toLowerCase();

  // 1. Search across all fields
  if (!filterFieldKey || filterFieldKey === "__ALL__") {
    return submissions.filter((sub) => {
      // Check document number & submitter
      if (sub.documentNumber && sub.documentNumber.toLowerCase().includes(lowerFilter)) return true;
      if (sub.submitterName && sub.submitterName.toLowerCase().includes(lowerFilter)) return true;
      if (sub.submitterEmail && sub.submitterEmail.toLowerCase().includes(lowerFilter)) return true;

      // Check each data field
      if (sub.data) {
        for (const [key, fieldData] of Object.entries(sub.data)) {
          const fieldConfig = fields.find((f) => f.key === key);
          if (matchesFilter(fieldConfig, fieldData, trimmedFilter)) {
            return true;
          }
        }
      }

      return false;
    });
  }

  // 2. Specific field filter
  const targetField = fields.find((f) => f.key === filterFieldKey);

  return submissions.filter((sub) => {
    const fieldData = sub.data?.[filterFieldKey];
    return matchesFilter(targetField, fieldData, trimmedFilter);
  });
}
